import React, { useRef } from "react";
import {
  EyeOff,
  Zap,
  Wallet,
  Clock,
  UserMinus,
  Hourglass,
  ClipboardList,
  Hammer,
  Rocket,
  LifeBuoy,
  ArrowRight,
  Headphones,
  Users,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import BinaryBackdrop from "../components/BinaryBackdrop";
import ScrollArrow from "../components/ScrollArrow";
const LOGO_URL = "/Logo.jpeg";
const PRIMARY_EMAIL = "hello@build-partner.de";
const HERO_EMAIL = "hello@build-partner.de";
const HERO_BENEFITS = [
  { icon: EyeOff, title: "White-label execution", desc: "We stay invisible." },
  { icon: Zap, title: "Fast turnaround", desc: "Days, not weeks." },
  { icon: Wallet, title: "Pay per task", desc: "No fixed costs." },
];

const PROBLEMS = [
  {
    icon: Clock,
    title: "Limited capacity",
    desc: "Projects delayed due to limited development capacity.",
  },
  {
    icon: UserMinus,
    title: "Hiring is expensive",
    desc: "Hiring developers is expensive and slow.",
  },
  {
    icon: Hourglass,
    title: "Clients won't wait",
    desc: "Clients expect fast delivery and won't wait.",
  },
];

const STEPS = [
  {
    icon: ClipboardList,
    title: "Client Requests",
    desc: "You send us tasks and requirements.",
  },
  {
    icon: Hammer,
    title: "We Build",
    desc: "Fast development with review and revisions.",
  },
  {
    icon: Rocket,
    title: "Launch",
    desc: "Final delivery ready for your client.",
  },
  {
    icon: LifeBuoy,
    title: "Ongoing Support",
    desc: "Updates, fixes, and improvements when needed.",
    optional: true,
  },
];

const WHY_US = [
  {
    icon: Wallet,
    title: "Pay per task",
    desc: "No fixed costs and no long-term commitments.",
  },
  {
    icon: Users,
    title: "No hiring",
    desc: "Skip recruitment and team management entirely.",
  },
  {
    icon: Zap,
    title: "Fast delivery",
    desc: "Most projects shipped in 48–72 hours.",
  },
  {
    icon: Headphones,
    title: "Ongoing support",
    desc: "We stay available after launch for updates.",
  },
  {
    icon: EyeOff,
    title: "Fully invisible",
    desc: "Your clients only see you. Always.",
  },
];

const WORK_SAMPLES = [
  {
    title: "SaaS Landing Page",
    image:
"https://via.placeholder.com/800x500?text=Project",},  
  {
    title: "Agency Website",
    image:
"https://via.placeholder.com/800x500?text=Project",},
  {
    title: "Portfolio Site",
    image:
"https://via.placeholder.com/800x500?text=Project",},
  {
    title: "Product Launch Page",
    image:
"https://via.placeholder.com/800x500?text=Project",},
];

const Hero = ({ headlineRef }) => (
  <section
    className="bp-container pt-16 sm:pt-20 pb-20 sm:pb-24 text-center"
    data-testid="hero-section"
  >
    <Link to="/" className="inline-block" data-testid="brand-home-link">
      <img
        src={LOGO_URL}
        alt="Build Partner Logo"
        className="mx-auto w-24 h-24 sm:w-28 sm:h-28 object-contain"
        data-testid="brand-logo"
        draggable="false"
      />
    </Link>

    <h1
      ref={headlineRef}
      className="mt-5 font-[Manrope] text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0A2540]"
      data-testid="hero-headline"
    >
      Build Partner
    </h1>

    <p
      className="bp-lead mt-5 max-w-2xl mx-auto"
      data-testid="hero-subheadline"
    >
      We build landing pages and websites for your clients, under your brand.
      No hiring, no overhead, no delays.
    </p>

    <div
      className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-7 sm:gap-10 max-w-3xl mx-auto"
      data-testid="hero-benefits"
    >
      {HERO_BENEFITS.map((b) => (
        <div
          key={b.title}
          className="flex flex-col items-center text-center gap-2"
          data-testid={`hero-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-slate-100 text-[#0A2540]">
            <b.icon className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-semibold text-slate-900 mt-3">{b.title}</p>
          <p className="text-sm text-slate-500">{b.desc}</p>
        </div>
      ))}
    </div>

    <div className="mt-12 flex justify-center">
      <a
        href={`mailto:${HERO_EMAIL}`}
        className="text-sm sm:text-base font-medium text-slate-700 hover:text-[#0A2540] transition-colors inline-flex items-center gap-2"
        data-testid="hero-secondary-email"
      >
        <Mail className="w-4 h-4" />
        {HERO_EMAIL}
      </a>
    </div>
  </section>
);

const Solution = ({ titleRef, heroHeadlineRef }) => (
  <section
    className="relative bp-container py-20 sm:py-24 text-center border-t border-slate-100"
    data-testid="solution-section"
  >
    {/* Arrow A: Hero "Build Partner" → Solution title */}
    <div className="hidden md:block absolute -top-[460px] right-4 lg:right-12 w-[260px] h-[520px] pointer-events-none z-0">
      <ScrollArrow
        d="M 230 18 C 230 200, 40 300, 145 506"
        viewBox="0 0 260 520"
        startRef={heroHeadlineRef}
        endRef={titleRef}
        testId="scroll-arrow-1"
      />
    </div>

    <span className="bp-eyebrow relative">Solution</span>
    <h2
      ref={titleRef}
      className="bp-h2 mt-3 max-w-3xl mx-auto relative"
      data-testid="solution-title"
    >
      Your White-Label Development Partner
    </h2>
    <p
      className="bp-lead mt-5 max-w-2xl mx-auto relative"
      data-testid="solution-text"
    >
      We work as an extension of your agency. You manage the client, we handle
      the development. Everything is delivered under your brand — we stay
      invisible.
    </p>
  </section>
);

const Services = () => (
  <section
    className="bp-container pb-20 sm:pb-24 text-center"
    data-testid="services-section"
  >
    <div className="max-w-3xl mx-auto border-y border-slate-200 py-8">
      <p
        className="text-base sm:text-lg font-medium text-slate-700"
        data-testid="services-text"
      >
        Landing pages and websites built for your clients under your brand.
      </p>
    </div>
  </section>
);

const Problem = ({ titleRef, solutionTitleRef }) => (
  <section
    className="relative bg-slate-50/60 border-y border-slate-200"
    data-testid="problem-section"
  >
    {/* Arrow B: Solution title → "What slows agencies down" */}
    <div className="hidden md:block absolute -top-[460px] left-4 lg:left-12 w-[260px] h-[520px] pointer-events-none z-0">
      <ScrollArrow
        d="M 30 18 C 30 200, 230 300, 70 506"
        viewBox="0 0 260 520"
        startRef={solutionTitleRef}
        endRef={titleRef}
        testId="scroll-arrow-2"
      />
    </div>

    <div className="bp-container py-20 sm:py-24 relative">
      <div className="max-w-2xl">
        <span className="bp-eyebrow">The reality</span>
        <h2 ref={titleRef} className="bp-h2 mt-3" data-testid="problem-title">
          What slows agencies down
        </h2>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PROBLEMS.map((p, i) => (
          <div
            key={p.title}
            className="bp-card"
            data-testid={`problem-card-${i + 1}`}
          >
            <div className="bp-icon-wrap mb-5">
              <p.icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <h3 className="bp-h3 mb-2">{p.title}</h3>
            <p className="bp-body">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = ({ titleRef, problemTitleRef }) => (
  <section
    className="relative bp-container py-24 sm:py-32"
    data-testid="how-it-works-section"
  >
    {/* Arrow C: "What slows agencies down" → "How it works" */}
    <div className="hidden md:block absolute -top-[480px] right-4 lg:right-12 w-[260px] h-[540px] pointer-events-none z-0">
      <ScrollArrow
        d="M 230 18 C 230 220, 40 320, 138 524"
        viewBox="0 0 260 540"
        startRef={problemTitleRef}
        endRef={titleRef}
        testId="scroll-arrow-3"
      />
    </div>

    <div className="text-center max-w-2xl mx-auto relative">
      <span className="bp-eyebrow">Process</span>
      <h2
        ref={titleRef}
        className="bp-h2 mt-3"
        data-testid="how-it-works-title"
      >
        How it works
      </h2>
      <p className="bp-lead mt-4">
        A simple, predictable workflow built for agency speed.
      </p>
    </div>

    <div className="mt-16 relative">
      {/* Desktop horizontal flow */}
      <div className="hidden md:grid grid-cols-7 items-stretch gap-0">
        {STEPS.map((step, idx) => (
          <React.Fragment key={step.title}>
            <div
              className="col-span-1 flex flex-col items-center text-center px-2"
              data-testid={`how-step-${idx + 1}`}
            >
              <div className="relative w-16 h-16 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-[0_2px_12px_rgba(10,37,64,0.06)]">
                <step.icon
                  className="w-7 h-7 text-[#0A2540]"
                  strokeWidth={1.75}
                />
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#0A2540] text-white text-[11px] font-semibold flex items-center justify-center">
                  {idx + 1}
                </span>
              </div>
              <h3 className="bp-h3 mt-5">
                {step.title}
                {step.optional && (
                  <span className="block text-xs font-normal text-slate-400 mt-1">
                    (optional)
                  </span>
                )}
              </h3>
              <p className="bp-body mt-2 max-w-[200px]">{step.desc}</p>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className="col-span-1 flex items-start justify-center pt-7"
                aria-hidden="true"
              >
                <div className="flex items-center w-full">
                  <span className="flex-1 h-px bg-slate-300" />
                  <ArrowRight className="w-5 h-5 text-slate-400 mx-1 flex-shrink-0" />
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile vertical flow */}
      <div className="md:hidden flex flex-col gap-8">
        {STEPS.map((step, idx) => (
          <div
            key={step.title}
            className="flex gap-5"
            data-testid={`how-step-mobile-${idx + 1}`}
          >
            <div className="flex flex-col items-center">
              <div className="relative w-14 h-14 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                <step.icon
                  className="w-6 h-6 text-[#0A2540]"
                  strokeWidth={1.75}
                />
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#0A2540] text-white text-[10px] font-semibold flex items-center justify-center">
                  {idx + 1}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <span className="flex-1 w-px bg-slate-200 mt-3" />
              )}
            </div>
            <div className="pb-2">
              <h3 className="bp-h3">
                {step.title}
                {step.optional && (
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    (optional)
                  </span>
                )}
              </h3>
              <p className="bp-body mt-1">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section
    className="bg-slate-50/60 border-y border-slate-200"
    data-testid="why-us-section"
  >
    <div className="bp-container py-20 sm:py-24">
      <div className="max-w-2xl">
        <span className="bp-eyebrow">Why us</span>
        <h2 className="bp-h2 mt-3" data-testid="why-us-title">
          Built for agencies that want to scale
        </h2>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {WHY_US.map((item, i) => (
          <div
            key={item.title}
            className="bp-card"
            data-testid={`why-us-card-${i + 1}`}
          >
            <div className="bp-icon-wrap mb-5">
              <item.icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <h3 className="bp-h3 mb-2">{item.title}</h3>
            <p className="bp-body">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WorkSamples = () => (
  <section
    className="bp-container py-24 sm:py-32"
    data-testid="work-samples-section"
  >
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
      <div>
        <span className="bp-eyebrow">Portfolio</span>
        <h2 className="bp-h2 mt-3" data-testid="work-samples-title">
          Recent work
        </h2>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
      {WORK_SAMPLES.map((s, i) => (
        <figure
          key={s.title}
          className="group"
          data-testid={`work-card-${i + 1}`}
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 aspect-[16/10]">
            <img
              src={s.image}
              alt={s.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
            />
          </div>
          <figcaption className="mt-4 flex items-center justify-between">
            <span className="text-base font-semibold text-slate-900">
              {s.title}
            </span>
            <span className="text-xs text-slate-400 tracking-widest uppercase">
              0{i + 1}
            </span>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const ClosingFinalCTA = () => (
  <section
    className="border-t border-slate-200 bg-[#0A2540] text-white"
    data-testid="closing-section"
  >
    <div className="bp-container py-24 sm:py-32 text-center">
      <h2
        className="font-[Manrope] text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold"
        data-testid="closing-headline"
      >
        We are your reliable tech partner
      </h2>
      <p
        className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-slate-300 leading-relaxed"
        data-testid="closing-text"
      >
        Stop worrying about development capacity. Start saying yes to more
        client projects.
      </p>

      <div className="mt-16 pt-12 border-t border-white/10">
        <p className="bp-eyebrow text-slate-400">Final step</p>
        <h3
          className="mt-3 font-[Manrope] text-3xl sm:text-4xl font-bold tracking-tight"
          data-testid="final-cta-headline"
        >
          Ready to scale?
        </h3>
        <a
          href={`mailto:${PRIMARY_EMAIL}`}
          className="block mt-5 text-base sm:text-lg text-slate-200 hover:text-white underline-offset-4 hover:underline transition-colors"
          data-testid="final-cta-email"
        >
          {PRIMARY_EMAIL}
        </a>
        <div className="mt-8">
          <a
            href={`mailto:${PRIMARY_EMAIL}?subject=New%20project%20with%20Build%20Partner`}
            className="inline-flex items-center justify-center gap-2 px-7 py-4 text-sm sm:text-base font-semibold text-[#0A2540] bg-white rounded-xl hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A2540] focus-visible:ring-white"
            data-testid="final-cta-button"
          >
            Start your first project
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  const heroHeadlineRef = useRef(null);
  const solutionTitleRef = useRef(null);
  const problemTitleRef = useRef(null);
  const howTitleRef = useRef(null);

  return (
    <div
      className="min-h-screen flex flex-col bg-white relative"
      data-testid="home-page"
    >
      <BinaryBackdrop />
      <div className="relative z-10 flex flex-col flex-1">
        <main className="flex-1">
          <Hero headlineRef={heroHeadlineRef} />
          <Solution
            titleRef={solutionTitleRef}
            heroHeadlineRef={heroHeadlineRef}
          />
          <Services />
          <Problem
            titleRef={problemTitleRef}
            solutionTitleRef={solutionTitleRef}
          />
          <HowItWorks
            titleRef={howTitleRef}
            problemTitleRef={problemTitleRef}
          />
          <WhyUs />
          <WorkSamples />
          <ClosingFinalCTA />
        </main>
        <Footer />
      </div>
    </div>
  );
}
